import { useMemo, useState } from 'react';

import { Box, Skeleton } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { groupBy } from 'lodash';
import ReactApexChart from 'react-apexcharts';
import { useQuery } from 'react-query';
import { If } from 'src/components/If';
import Subtitle from 'src/components/Subtitle';
import { getAmortizationRedemptionsDataCharts } from 'src/services/operation/operationFidc';
import { toCurrency } from 'src/utils/number';

import { GroupButtonPeriod, Period } from '../../components/GroupButtonPeriod';
import {
  colors,
  getLimitZoomMountedHandler,
  getPeriodDate,
  getQuotaKey,
  getQuotaLabel,
  locale,
  noData,
  QuotaAccumulator,
} from '../utils';

const defaultPeriodInDays = 180;

export function AmortizationRedemptionsCharts({ hashId }: { hashId: string }) {
  const [selectedQuotas, setSelectedQuotas] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] =
    useState<Period>(defaultPeriodInDays);

  const { data, isLoading } = useQuery({
    queryKey: ['amortization-redemptions-charts', hashId, selectedPeriod],
    queryFn: () =>
      getAmortizationRedemptionsDataCharts(
        hashId,
        getPeriodDate(selectedPeriod),
      ),
    onSuccess: (data) => {
      const groupedByQuotaSerie = groupBy(data, getQuotaKey);
      setSelectedQuotas(Object.keys(groupedByQuotaSerie));
    },
    enabled: !!hashId,
    refetchOnWindowFocus: false,
  });

  const handlePeriodChange = (days: Period) => {
    setSelectedPeriod(days);
  };

  const chartData = useMemo(() => {
    if (!data || data.length === 0 || selectedQuotas.length === 0) {
      return { series: [] };
    }

    const filteredData = data.filter((item) =>
      selectedQuotas.includes(getQuotaKey(item)),
    );

    const allDates = [...new Set(filteredData.map((item) => item.data))].sort();

    const groupedByTipoQuota = filteredData.reduce(
      (acc: QuotaAccumulator, item) => {
        const key = `${item.tipo}|${getQuotaKey(item)}`;
        if (!acc[key]) {
          acc[key] = {
            name: `${item.tipo} - ${getQuotaLabel(item.cota, item.serie)}`,
            values: {},
          };
        }
        acc[key].values[item.data] = item.montante;
        return acc;
      },
      {} as QuotaAccumulator,
    );

    const series = Object.values(groupedByTipoQuota).map(
      ({ name, values }) => ({
        name,
        data: allDates.map((date) => {
          const timestamp = new Date(date).getTime();
          const value = values[date];
          return {
            x: timestamp,
            y: value || null,
          };
        }),
      }),
    );

    return { series };
  }, [data, selectedQuotas]);

  const options: ApexOptions = {
    series: chartData.series,
    colors: colors,
    chart: {
      type: 'bar',
      defaultLocale: 'pt-BR',
      locales: [locale],
      zoom: {
        type: 'xy',
        enabled: true,
        autoScaleYaxis: false,
      },
      toolbar: {
        autoSelected: 'zoom',
      },
      events: {
        updated: getLimitZoomMountedHandler(),
      },
      stacked: false,
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 0,
      hover: {
        size: 6,
      },
    },
    legend: {
      show: true,
      showForSingleSeries: true,
      position: 'top',
      horizontalAlign: 'center',
      floating: false,
      offsetY: 20,
      height: 50,
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          if (val === null || val === undefined) return '';
          return val.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      },
      title: {
        text: 'Amortização/Resgate (R$)',
        style: {
          fontFamily: 'Inter',
        },
      },
    },
    xaxis: {
      type: 'datetime',
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        format: 'dd/MM/yyyy',
      },
      y: {
        formatter: (value) => (value ? toCurrency(value) : ''),
      },
    },
    noData: noData,
  };
  return (
    <Box
      sx={{
        '.apexcharts-menu-item.exportCSV': { display: 'none' },
        '.apexcharts-legend': {
          maxHeight: '80px',
          overflowY: 'auto !important',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
          },
        },
      }}>
      <Subtitle title="Amortizações e Resgates" />
      <If condition={isLoading}>
        <Skeleton variant="rectangular" animation="wave" height={420} />
      </If>
      <If condition={!isLoading}>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          gap={2}
          alignItems="flex-end">
          <GroupButtonPeriod
            selectedPeriod={selectedPeriod}
            handlePeriodChange={handlePeriodChange}
          />
        </Box>
        <ReactApexChart
          type="bar"
          series={chartData.series}
          options={options}
          height={420}
        />
      </If>
    </Box>
  );
}
